import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, switchMap, finalize, filter, take } from 'rxjs/operators';
import { LoginResponse } from '../constants/constants';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { environment } from '../../environment/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private apiService: ApiService, private dataService: DataService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = this.dataService.getAccessToken();

    // Clone the request and attach the token if available
    let modifiedReq = req;
    if (accessToken) {
      modifiedReq = this.addToken(req, accessToken);
    }

    return next.handle(modifiedReq).pipe(
      catchError((error) => {
        // Handle 401 errors (unauthorized)
        if (error instanceof HttpErrorResponse && error.status === 401) {
          if(modifiedReq.url.includes(environment.baseUrl+'refresh')){
            console.error('Received 401 from refresh url, hence logging out')
            this.apiService.logout();
          }else {
            return this.handle401Error(req, next);
          }
        }

        return throwError(() => error);
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.dataService.getRefreshToken();
      if (refreshToken) {
        return this.apiService.refreshToken(refreshToken).pipe(
          catchError(err=>{
            console.error('Received error while refreshing token, hence logging out')
            this.apiService.logout();
            return EMPTY;
          }),
          switchMap((newTokens: LoginResponse) => {
            this.apiService.saveLogin(newTokens);
            this.refreshTokenSubject.next(newTokens.access_token);
            return next.handle(this.addToken(req, newTokens.access_token));
          }),
          finalize(() => {
            this.isRefreshing = false;
          })
        );
      }
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addToken(req, token!)))
    );
  }
}

// Ensure to register this interceptor in your app module
// Add it to the HTTP_INTERCEPTORS array in your app.module.ts
// Example:
// providers: [
//   { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
// ]
